import { useCallback, useEffect, useRef, useState } from "react";
import { OAuthServiceType } from "@common/constants";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useWindowMessage } from "@hooks/useWindowMessage";
import { OAuthAuthorizationResponse, triggerAuthorization } from "@lib/oauth";

const POPUP_POLL_MS = 500;
// The callback page posts its message right before it closes itself, so the
// pending state outlives the window by a moment.
const POPUP_CLOSE_GRACE_MS = 1_000;

interface UseOAuthPopupOptions {
    service: OAuthServiceType;
    serviceLabel: string;
    onAuthorized: (response: OAuthAuthorizationResponse) => void;
}

interface UseOAuthPopupResult {
    open: () => void;
    isPopupOpen: boolean;
}

export function useOAuthPopup({
    service,
    serviceLabel,
    onAuthorized,
}: UseOAuthPopupOptions): UseOAuthPopupResult {
    const { warning } = useToaster();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    // Only the result carrying the latest attempt's state is accepted.
    const attemptStateRef = useRef<string | null>(null);
    const popupRef = useRef<Window | null>(null);
    const popupPollRef = useRef<number | null>(null);
    const closeGraceRef = useRef<number | null>(null);

    const clearTimers = useCallback(() => {
        if (popupPollRef.current !== null) {
            window.clearInterval(popupPollRef.current);
            popupPollRef.current = null;
        }
        if (closeGraceRef.current !== null) {
            window.clearTimeout(closeGraceRef.current);
            closeGraceRef.current = null;
        }
    }, []);

    useEffect(() => clearTimers, [clearTimers]);

    const handleAuthorized = useCallback(
        (response: OAuthAuthorizationResponse) => {
            if (
                attemptStateRef.current === null ||
                response.state !== attemptStateRef.current
            ) {
                return;
            }
            attemptStateRef.current = null;
            clearTimers();
            setIsPopupOpen(false);
            onAuthorized(response);
        },
        [clearTimers, onAuthorized],
    );

    useWindowMessage<OAuthAuthorizationResponse>({
        scope: service,
        handler: handleAuthorized,
    });

    const open = useCallback(() => {
        if (popupRef.current && !popupRef.current.closed) {
            return;
        }
        clearTimers();
        const state = crypto.randomUUID();
        const popup = triggerAuthorization(service, { state });
        if (!popup) {
            setIsPopupOpen(false);
            warning(
                `Your browser blocked the ${serviceLabel} sign-in window. Allow pop-ups for Kick and try again.`,
            );
            return;
        }
        popupRef.current = popup;
        attemptStateRef.current = state;
        setIsPopupOpen(true);
        const poll = window.setInterval(() => {
            if (!popup.closed) {
                return;
            }
            window.clearInterval(poll);
            if (popupPollRef.current === poll) {
                popupPollRef.current = null;
            }
            closeGraceRef.current = window.setTimeout(() => {
                closeGraceRef.current = null;
                setIsPopupOpen(false);
            }, POPUP_CLOSE_GRACE_MS);
        }, POPUP_POLL_MS);
        popupPollRef.current = poll;
    }, [service, serviceLabel, warning, clearTimers]);

    return { open, isPopupOpen };
}
